// src/components/PrintDesigner/printData.js
// 1) تكميل بيانات الطباعة من بطاقة الشركة (البروفايل) وبطاقة العميل
// 2) قائمة القوالب المتاحة لنوع الفاتورة + القالب المختار
import { useMemo, useState } from "react";
import html2pdf from "html2pdf.js";
import axios from "../../axios";
import printLayoutApi from "../../api/printLayoutApi";
import { parseTemplateSet, PAPER, templatesFor, isVoucherType } from "./printTemplates";

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);
const filled = (v) => v != null && String(v).trim() !== "";

/** يدمج الكائنات بالترتيب؛ القيمة الفارغة لا تمسح قيمة موجودة. الكائنات الداخلية (مثل nationalAddress) تُسطَّح */
function mergeCards(...cards) {
  const out = {};
  const put = (k, v) => { if (filled(v) && !isObj(v) && !Array.isArray(v)) out[k] = v; };
  cards.filter(isObj).forEach(c => {
    Object.entries(c).forEach(([k, v]) => {
      if (isObj(v)) Object.entries(v).forEach(([k2, v2]) => { if (!filled(out[k2])) put(k2, v2); });
      else put(k, v);
    });
  });
  return out;
}

const get = (url) => axios.get(url).then(r => r.data).catch(() => null);

// ذاكرة مؤقتة لبطاقة الشركة طوال الجلسة — لا نطلبها من الخادم مع كل فاتورة
let companyCache = null;

/**
 * يكمّل بيانات الطباعة من بطاقة الشركة وبطاقة العميل — فقط عند الحاجة.
 * دالة الطباعة الجديدة في الخادم ترجع البطاقتين كاملتين، فلا نرسل أي طلب إضافي.
 * (الطلبات الإضافية تبقى احتياطًا لخادم قديم)
 */
export async function enrichPrintData(data) {
  if (!data) return data;
  const companyId = parseInt(localStorage.getItem("companyId"), 10);
  const cust = data.customer || {};
  const customerId = data.customerId ?? cust.id ?? cust.customerId;

  // الخادم الجديد يرجع حقول البطاقة كاملة (مثل crn / vatNo / buildingNo)
  const companyComplete = isObj(data.company) && Object.keys(data.company).length > 8;
  const customerComplete = !isObj(data.customer) || Object.keys(data.customer).length > 6;

  const needCompany = !companyComplete && companyId;
  const needCustomer = !customerComplete && companyId && customerId;

  if (!needCompany && !needCustomer) return data;

  const [profile, customerCard] = await Promise.all([
    needCompany
      ? (companyCache?.id === companyId
          ? companyCache.data
          : get(`/CompanyProfile/public/${companyId}`).then(d => { companyCache = { id: companyId, data: d }; return d; }))
      : null,
    needCustomer ? get(`/Customer/${customerId}`) : null,
  ]);

  return {
    ...data,
    company: needCompany ? mergeCards(profile, data.company) : data.company,
    customer: needCustomer && isObj(customerCard) ? mergeCards(customerCard, data.customer) : data.customer,
  };
}

// ───────────────────────── القوالب ─────────────────────────
const storageKey = (type) => `printTemplate:${type ?? "all"}`;
const readLS = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const writeLS = (k, v) => { try { localStorage.setItem(k, v); } catch { /* تجاهل */ } };

/** القوالب المحفوظة لنوع الفاتورة (وإن لم توجد: تصاميم النظام) + القالب المختار
 *  افتراضيًا يبدأ دائمًا بالقالب ★ المحدد في المصمم (rememberLast=true يتذكر آخر اختيار) */
export function useInvoiceTemplates(layout, typeKey, { rememberLast = false } = {}) {
  const options = useMemo(() => {
    const saved = parseTemplateSet(layout);
    if (saved.length) return saved;
    const voucher = isVoucherType(typeKey);
    const builtin = templatesFor(voucher ? "voucher" : "invoice").map(t => ({ id: `builtin:${t.key}`, name: t.label, isDefault: false, layout: t.make() }));
    const defId = voucher ? "builtin:v-a4" : Number(typeKey) === 11 ? "builtin:80mm" : "builtin:full";
    const def = builtin.find(b => b.id === defId) || builtin[0];
    def.isDefault = true;
    return builtin;
  }, [layout, typeKey]);

  // ?template= في الرابط أولًا، ثم (اختياريًا) آخر اختيار لهذا النوع، ثم الافتراضي ★
  const [wanted] = useState(() =>
    new URLSearchParams(window.location.search).get("template") || (rememberLast ? readLS(storageKey(typeKey)) : null));
  const [chosen, setChosen] = useState(null);

  const current =
    options.find(o => o.id === chosen) ||
    (wanted && options.find(o => o.id === wanted || o.name === wanted)) ||
    options.find(o => o.isDefault) || options[0] || null;

  const pick = (id) => { setChosen(id); if (rememberLast) writeLS(storageKey(typeKey), id); };
  return { options, current, pick };
}

// ───────────────────────── تحميل فاتورة للطباعة ─────────────────────────
/** يرجع { data, layout }: بيانات الفاتورة مكمَّلة من البطاقات + قوالب نوعها المحفوظة */
export async function loadInvoiceForPrint(invGlobalID) {
  const invoiceData = (await axios.get(`/Invoice/print/${invGlobalID}`)).data;
  const typeId = invoiceData?.invoiceTypeId || invoiceData?.invoiceType;
  const companyId = parseInt(localStorage.getItem("companyId"), 10);
  const [data, layoutRes] = await Promise.all([
    enrichPrintData(invoiceData),
    typeId ? printLayoutApi.get(companyId, typeId).catch(() => null) : null,
  ]);
  let layout = null;
  try { layout = layoutRes?.data?.layoutJson ? JSON.parse(layoutRes.data.layoutJson) : null; } catch { layout = null; }
  return { data, layout };
}

// ───────────────────────── PDF ─────────────────────────
/** html2pdf جاهز للحفظ أو التحويل إلى Blob — el هو عنصر .pl-page المرسوم */
export function invoicePdf(el, template, data) {
  const paper = PAPER[template?.layout?.page?.size] || PAPER.A4;
  const heightMm = Math.ceil(el.offsetHeight / (96 / 25.4)) + 2;
  return html2pdf().set({
    margin: 0,
    filename: `فاتورة_${data?.invoiceNumber ?? ""}.pdf`,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true, scrollY: 0, scrollX: 0 },
    jsPDF: { unit: "mm", format: paper.thermal ? [paper.w, heightMm] : [paper.w, paper.h], orientation: "portrait" },
    pagebreak: { mode: ["css", "legacy"] },
  }).from(el);
}

/** مشاركة ملف PDF (أو تنزيله إن لم يدعم المتصفح المشاركة) — يرجع true إذا شارك */
export async function sharePdfBlob(blob, invoiceNumber) {
  const file = new File([blob], `Invoice_${invoiceNumber}.pdf`, { type: "application/pdf" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    await navigator.share({ files: [file], title: `فاتورة رقم ${invoiceNumber}`, text: `مرفق نسخة PDF من الفاتورة رقم ${invoiceNumber}` });
    return true;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = file.name;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
  return false;
}

// ───────────────────────── السندات ─────────────────────────
let profileCache = null;

/** بيانات السند للطباعة: السند نفسه + بطاقة المنشأة كاملة (مع الشعار) — تُجلب مرة واحدة في الجلسة */
export async function loadVoucherForPrint(voucher, companyInfo) {
  const companyId = parseInt(localStorage.getItem("companyId"), 10);
  let profile = profileCache?.id === companyId ? profileCache.data : null;
  if (!profile && companyId) {
    profile = await get(`/CompanyProfile/${companyId}`);
    if (profile) profileCache = { id: companyId, data: profile };
  }
  const layoutRes = companyId && voucher?.voucherType
    ? await printLayoutApi.get(companyId, Number(voucher.voucherType)).catch(() => null)
    : null;
  let layout = null;
  try { layout = layoutRes?.data?.layoutJson ? JSON.parse(layoutRes.data.layoutJson) : null; } catch { layout = null; }

  const p = profile || {};
  const company = mergeCards(p, companyInfo, {
    name: p.nameAr, taxNumber: p.vatNo, crNumber: p.crn,
    logoUrl: p.logoImage ? `data:image/png;base64,${p.logoImage}` : companyInfo?.logoUrl,
  });
  return { data: { ...voucher, company }, layout };
}
